<template>
  <modal size="modal-lg" @close="$emit('close')">
    <template v-slot:title>
      Acordo
    </template>

    <template v-slot:body>
      <p>Regras de acordo:</p>
      <agreement-rules-table :agreement-rules="agreementRules" :read-only="true" class="debt-agreement-rules__table" />

      <p>Selecione as mensalidades que serão acordadas:</p>
      <input-bills-list v-model="parsedInputBills" />

      <div class="row">
        <div class="col-md-4">
          <label>Valor do acordo</label>
          <div>{{ fullValue | formatCurrency }}</div>
        </div>
        <div class="col-md-8">
          <div class="form-group">
            <label>Forma de pagamento</label>
            <select class="form-control" v-model="paymentMethod">
              <option v-for="(method, index) in paymentMethodsList" :key="index" :value="method.key">
                {{ method.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="alert alert-danger" role="alert" v-if="errors.length">
        <ul v-if="Array.isArray(errors)">
          <li v-for="(error, index) in errors" :key="index">
            {{ error }}
          </li>
        </ul>
        <template v-else>
          {{ errors }}
        </template>
      </div>

      <template v-if="fullValue > 0 && paymentMethod">
        <payment-entry
          :full-value="fullValue"
          @change="updatePaymentEntryOptions"
          v-if="paymentMethod === 'entry-plus-installments'"
        />

        <installments
          :full-value="installmentsFullValue"
          :payment-method="paymentMethod"
          @change="updateInstallmentsOptions"
          v-if="!!paymentMethod"
        />

        <cards
          @change="updateCardsOptions"
          v-if="paymentMethod === 'card'"
        />

        <div class="form-group">
          <label>Observações</label>
          <input type="text" class="form-control" placeholder="Observações" v-model="observations">
        </div>
      </template>
    </template>

    <template v-slot:footer>
      <div class="footer-options">
        <div>
          <template v-if="canEnableCreationButton">
            Atenção! Revise as informações antes de prosseguir.
          </template>
        </div>
        <div>
          <button
            class="btn btn-primary js-submit-button"
            @click="createDebtAgreement()"
            :disabled="!canEnableCreationButton"
          >
            Criar acordo
          </button>
          <button class="btn btn-default" @click="$emit('close')">
            Fechar
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>
<style lang="scss" scoped>
  @import 'assets/stylesheets/utils/_spacing.sass';

  .debt-agreement-rules__table {
    margin-bottom: $space-m;
  }
</style>
<script>
import moment from 'moment';
import 'moment/locale/pt-br';
import DebtAgreementsService from 'services/DebtAgreementsService';
import Modal from 'components/Modal';
import AgreementRulesTable from 'components/AgreementRulesTable';
import DebtAgreementRulesService from 'services/DebtAgreementRulesService';
import InputBillsList from './InputBillsList';
import Installments from './Installments';
import PaymentEntry from './PaymentEntry';
import Cards from './Cards';

export default {
  components: {
    InputBillsList,
    Installments,
    PaymentEntry,
    Cards,
    Modal,
    AgreementRulesTable,
  },
  props: {
    inputBills: {
      type: Array,
      default: () => [],
    },
    institutionId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      agreementRules: [],
      parsedInputBills: [],
      paymentEntry: null,
      installmentsOptions: null,
      cardsOptions: null,
      observations: null,
      paymentMethod: null,
      paymentMethodsList: [
        {
          key: 'entry-plus-installments',
          label: 'Entrada + parcelas iguais',
        },
        {
          key: 'installments',
          label: 'Parcelas iguais',
        },
        {
          key: 'card',
          label: 'Cartão de crédito',
        },
        {
          key: 'pix',
          label: 'Pix',
        },
      ],
      errors: [],
    };
  },
  watch: {
    paymentMethod() {
      if (this.paymentMethod === 'installments') {
        this.paymentEntry = null;
      }
    },
  },
  computed: {
    selectedBills() {
      return this.parsedInputBills.filter(bill => bill.selected);
    },
    fullValue() {
      if (this.selectedBills.length > 0) {
        return this.selectedBills.reduce((a, b) => a + b.value, 0);
      }
      return 0;
    },
    installmentsFullValue() {
      if (this.paymentMethod === 'entry-plus-installments' && !!this.paymentEntry) {
        return this.fullValue - parseFloat(this.paymentEntry.value);
      }
      return this.fullValue;
    },
    paymentEntryValid() {
      if (this.paymentMethod !== 'entry-plus-installments') {
        return true;
      }

      return !!this.paymentEntry
        && !!this.paymentEntry.value
        && !!this.paymentEntry.percentage
        && !!this.paymentEntry.expiryDate;
    },
    installmentsOptionsValid() {
      return !!this.installmentsOptions
        && !!this.installmentsOptions.installments
        && (this.paymentMethod === 'card' ? true : !!this.installmentsOptions.startDate);
    },
    cardsOptionsValid() {
      if (this.paymentMethod !== 'card') {
        return true;
      }

      return !!this.cardsOptions && !!this.cardsOptions.card;
    },
    canEnableCreationButton() {
      return !!this.fullValue
        && !!this.paymentEntryValid
        && !!this.installmentsOptionsValid
        && !!this.cardsOptionsValid
        && !!this.paymentMethod;
    },
  },
  methods: {
    parseBills() {
      this.parsedInputBills = this.inputBills.map(bill => ({
        ...bill,
        selected: false,
      }));
    },
    parseDate(date) {
      return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
    },
    createDebtAgreement() {
      if (this.canEnableCreationButton) {
        this.$loader.show();
        const inputBillIds = this.getInputBills();
        const options = {
          payment_type: this.getPaymentType(),
          installments: this.getInstallments(),
          installment_amounts: this.getInstallmentAmounts(),
          due_dates: this.getInstallmentDates(),
          observations: this.observations,
        };

        if (this.paymentMethod === 'card') {
          Object.assign(options, {
            payment_method: this.cardsOptions.card.card_type,
            student_card_id: this.cardsOptions.card.id,
          });
        }

        DebtAgreementsService.create(inputBillIds, options)
          .then(() => {
            this.$alert.notifySuccess('Acordo criado com sucesso!');
            this.$emit('close');
            this.$emit('created');
          })
          .catch((error) => {
            this.errors = error.response.data.message;
          })
          .finally(() => this.$loader.hide());
      }
    },
    getInstallments() {
      return this.installmentsOptions.installments + (this.paymentEntry ? 1 : 0);
    },
    getInstallmentAmounts() {
      return this.paymentEntry ? [this.paymentEntry.value] : [];
    },
    getInstallmentDates() {
      if (this.paymentMethod === 'card') {
        return null;
      }

      const dates = [this.parseDate(this.installmentsOptions.startDate)];
      if (this.paymentEntry) {
        dates.unshift(this.parseDate(this.paymentEntry.expiryDate));
      }

      return dates;
    },
    getInputBills() {
      return this.selectedBills.map(bill => bill.id);
    },
    getPaymentType() {
      return this.paymentMethod === 'card' ? 'immediate' : 'delayed';
    },
    updatePaymentEntryOptions(options) {
      this.paymentEntry = options;
    },
    updateInstallmentsOptions(options) {
      this.installmentsOptions = options;
    },
    updateCardsOptions(options) {
      this.cardsOptions = options;
    },
  },
  mounted() {
    this.parseBills();
    this.$loader.show();
    DebtAgreementRulesService.getDebtAgreementRules(this.institutionId)
      .then((agreementRules) => {
        this.agreementRules = agreementRules;
      })
      .catch(() => {
        this.$alert.notifyAlert('Erro ao buscar informações, tente novamente mais tarde');
      })
      .finally(() => {
        this.$loader.hide();
      });
  },

};
</script>

<style lang="scss" scoped>
.footer-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
